import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, LayoutDashboard, ShieldCheck, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/our-testing", label: "Our Testing" },
  { to: "/sample-report", label: "Sample Report" },
  { to: "/why-trust-us", label: "Why Trust Us" },
  { to: "/faq", label: "FAQ" },
  { to: "/about", label: "About" },
];

export function Logo({ light = false }) {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
      <span className="relative grid h-8 w-8 place-items-center rounded-md bg-navy">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M9 3v6l-5 8.5A2.5 2.5 0 0 0 6.2 21h11.6a2.5 2.5 0 0 0 2.2-3.5L15 9V3" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 3h8" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-crimson ring-2 ring-cream" />
      </span>
      <span className={`font-heading text-xl font-bold tracking-tight ${light ? "text-white" : "text-navy"}`}>
        test<span className="font-serif italic font-medium">my</span>supps
      </span>
    </Link>
  );
}

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="sticky top-0 z-50">
      <div className="bg-navy py-2 text-center text-[11px] font-semibold uppercase tracking-[0.15em] text-white" data-testid="top-banner">
        Independent &nbsp;•&nbsp; Lab Tested &nbsp;•&nbsp; 7-Day Turnaround
      </div>
      <header className="border-b border-border/70 bg-cream/90 backdrop-blur-xl" data-testid="navbar">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <Logo />
          <div className="hidden items-center gap-7 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-navy ${isActive ? "text-navy" : "text-navy/60"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="hidden items-center gap-3 lg:flex">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1.5 text-sm font-medium text-navy hover:text-navy-dark">
                    <ShieldCheck className="h-4 w-4" /> Admin
                  </Link>
                )}
                <Link to="/dashboard" data-testid="nav-dashboard" className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-secondary">
                  <LayoutDashboard className="h-4 w-4" /> Dashboard
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout" className="grid h-9 w-9 place-items-center rounded-full text-navy/50 hover:bg-secondary">
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/pricing" data-testid="nav-getstarted" className="rounded-lg bg-crimson px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-crimson-dark">Get Started</Link>
                <Link to="/login" data-testid="nav-login" className="flex items-center gap-1 text-sm font-semibold text-navy hover:text-crimson">Log in <ArrowUpRight className="h-4 w-4" /></Link>
              </>
            )}
          </div>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="h-6 w-6 text-navy" /> : <Menu className="h-6 w-6 text-navy" />}
          </button>
        </nav>
        {open && (
          <div className="border-t border-border bg-cream px-6 py-4 lg:hidden" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-navy/80 hover:bg-secondary">
                  {l.label}
                </Link>
              ))}
              <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3">
                {user ? (
                  <>
                    {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-navy">Admin Console</Link>}
                    <Link to="/dashboard" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-navy">Dashboard</Link>
                    <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-navy/60">Log out</button>
                  </>
                ) : (
                  <>
                    <Link to="/pricing" onClick={() => setOpen(false)} className="rounded-lg bg-crimson px-5 py-2.5 text-center text-sm font-bold text-white">Get Started</Link>
                    <Link to="/login" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-navy">Log in</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </div>
  );
}
